import { useIonRouter } from '@ionic/react';
import { Alert, AlertDescription } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Smartphone } from 'lucide-react';
import { useCallback, type ReactNode } from 'react';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { ProfileEditBasicSection } from '@/core/components/profile/edit/profile-edit-basic-section';
import { ProfileEditLeadFormSection } from '@/core/components/profile/edit/profile-edit-lead-form-section';
import {
  ProfileEditModulesSection,
  ProfileEditPreviewColumn,
  ProfileEditPreviewDialog,
} from '@/core/components/profile/edit/profile-edit-modules-and-preview';
import {
  ProfileEditErrorState,
  ProfileEditLoadingState,
} from '@/core/components/profile/edit/profile-edit-page-states';
import { ProfileEditSettingsSection } from '@/core/components/profile/edit/profile-edit-settings-section';
import { useProfileEdit } from '@/core/components/profile/edit/use-profile-edit';
import { ProfileTemplateBanner } from '@/core/components/profile-templates/profile-template-banner';
import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import { AnimatedUnderlineTabs } from '@/core/components/ui/tabs-29';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { shellPaths } from '@/core/router/app-paths';
import { AppIonPage } from '@/core/components/app-ion-page';

function ProfileEditBreadcrumb({
  workLabel,
  profilesLabel,
}: {
  workLabel: string;
  profilesLabel: string;
}) {
  return (
    <>
      {workLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {profilesLabel}
    </>
  );
}

export function ProfileEditPage() {
  const ionRouter = useIonRouter();
  const backRouter = useIonBackRouter();
  const goBack = (fallback: string) => goBackOrFallback(backRouter, fallback);
  const replaceWith = useCallback(
    (path: string) => {
      ionRouter.push(path, 'forward', 'replace');
    },
    [ionRouter],
  );

  const edit = useProfileEdit({ replaceWith });
  const handleBack = () => goBack(shellPaths.profiles);
  const editDict = edit.dict.profiles.edit;
  const breadcrumb = (
    <ProfileEditBreadcrumb
      workLabel={edit.dict.nav.navDomainWork}
      profilesLabel={edit.dict.nav.profiles}
    />
  );

  // One stable AppIonPage for the whole route. Swapping distinct page roots
  // (loading → content) while StackManager still owns `ion-page-invisible`
  // leaves the entering page at opacity:0 — blank shell, a11y still populated.
  let body: ReactNode;
  let previewDialog: ReactNode = null;

  if (edit.showLoading) {
    body = (
      <ProfileEditLoadingState
        title={editDict.title}
        description={editDict.description}
        breadcrumb={breadcrumb}
        onBack={handleBack}
      />
    );
  } else if (edit.profile == null) {
    body = (
      <ProfileEditErrorState
        title={editDict.title}
        description={editDict.description}
        breadcrumb={breadcrumb}
        onBack={handleBack}
        errorMessage={edit.profileLoadError?.message || editDict.error}
        retryLabel={editDict.profileLoadRetry}
        onRetry={() => {
          void edit.refetch();
        }}
      />
    );
  } else {
    const overrideShared = {
      fieldLockedHintMessage: edit.fieldLockedHintMessage,
      overrideHintMessage: edit.overrideHintMessage,
      resetLabel: edit.templateOverrideLabels.reset,
      templateValueLabel: edit.templateOverrideLabels.templateValue,
      getFieldOverrideDisplay: edit.getFieldOverrideDisplay,
      onResetField: edit.resetFieldToTemplate,
    };

    const tabs = [
      {
        name: editDict.basicInformation.title,
        value: 'basic',
        content: (
          <ProfileEditBasicSection
            dict={editDict.basicInformation}
            formValues={edit.formValues}
            errors={edit.errors}
            register={edit.register}
            setValue={edit.setValue}
            isLocked={edit.isLocked}
            {...overrideShared}
          />
        ),
      },
      {
        name: editDict.leadForm.title,
        value: 'leadForm',
        content: (
          <ProfileEditLeadFormSection
            leadFormDict={editDict.leadForm}
            leadsWorkspaceDict={editDict.leadsWorkspace}
            formValues={edit.formValues}
            setValue={edit.setValue}
            isLocked={edit.isLocked}
            showLeadsWorkspaceCard={
              !edit.currentWorkspace?.leadsAlwaysSharedWithWorkspace
            }
            {...overrideShared}
          />
        ),
      },
      {
        name: 'Module',
        value: 'modules',
        content: (
          <ProfileEditModulesSection
            modules={edit.modules}
            onModulesChange={edit.setModules}
            isWorkspaceProfile={
              !!edit.currentWorkspace && edit.hasTeamMembership
            }
            showManageTeamLink={edit.canManageWorkspace}
            readOnly={edit.formSyncBlocked}
            structureReadOnly={edit.templateGoverned}
            templateModules={edit.templateModules}
            fieldLockedHintMessage={edit.fieldLockedHintMessage}
            isModuleCustomized={edit.isModuleCustomized}
            onResetModuleToTemplate={edit.handleResetModuleToTemplate}
            overrideHintMessage={edit.overrideHintMessage}
            templateOverrideLabels={{
              hint: edit.overrideHintMessage,
              reset: edit.templateOverrideLabels.reset,
              templateValue: edit.templateOverrideLabels.templateValue,
              emptyValue: edit.templateOverrideLabels.emptyValue,
              moduleNoContent: edit.templateOverrideLabels.moduleNoContent,
              moduleEntries: edit.templateOverrideLabels.moduleEntries,
              entryFields: {
                emptyValue: edit.templateOverrideLabels.emptyValue,
                ...edit.templateOverrideLabels.entryFields,
              },
            }}
          />
        ),
      },
      {
        name: editDict.settings.title,
        value: 'settings',
        content: (
          <ProfileEditSettingsSection
            dict={editDict.settings}
            formValues={edit.formValues}
            errors={edit.errors}
            setValue={edit.setValue}
            resolvedAccentColor={edit.resolvedAccentColor}
            resolvedShowUp2promoBranding={edit.resolvedShowUp2promoBranding}
            isLocked={edit.isLocked}
            formSyncBlocked={edit.formSyncBlocked}
            entitlementsLoading={edit.entitlementsLoading}
            canCustomizeAccent={edit.canCustomizeAccent}
            canHideBranding={edit.canHideBranding}
            accentCtaLabel={edit.dict.publicProfile.saveContact}
            {...overrideShared}
          />
        ),
      },
    ];

    const previewProps = {
      username: edit.user?.username,
      workspaceId: edit.currentWorkspace?.id || null,
      workspaceUsername: edit.currentWorkspace?.username ?? null,
      previewProfileData: edit.previewProfileData,
    };

    body = (
      <AppPageContent>
        <AppPageLayout className="min-w-0 max-w-full md:pb-4">
          <PageHeader
            title={editDict.title}
            breadcrumb={breadcrumb}
            onBack={handleBack}
            actions={
              <Button
                variant="ghostAccent"
                size="md"
                className="gap-2 shrink-0 md:hidden"
                onClick={() => edit.setShowPreview(true)}
              >
                <Smartphone className="h-4 w-4" />
                Vorschau
              </Button>
            }
          />
          <div className="relative flex min-w-0 flex-1 flex-col gap-4 md:gap-5">
            <AutosaveStatusCue
              status={edit.autosaveStatus}
              className="pointer-events-none absolute left-0 top-0 z-10 -translate-y-full"
            />
            {edit.saveError ? (
              <Alert variant="destructive" className="min-w-0 w-full">
                <AlertDescription>{edit.saveError}</AlertDescription>
              </Alert>
            ) : null}

            <ProfileTemplateBanner
              templateId={edit.governedTemplateId}
              syncStatus={edit.syncStatus}
              forcePending={edit.formSyncBlocked}
              dict={{
                label: editDict.templateBanner.label,
                linkedDescription: editDict.templateBanner.linkedDescription,
                syncPending: editDict.templateBanner.syncPending,
                statusActive: editDict.templateBanner.statusActive,
                statusSyncing: editDict.templateBanner.statusSyncing,
              }}
            />

            <AnimatedUnderlineTabs
              tabs={tabs}
              value={edit.activeTab}
              onValueChange={edit.setActiveTab}
              className="w-full"
              rightColumn={<ProfileEditPreviewColumn {...previewProps} />}
            />
          </div>
        </AppPageLayout>
      </AppPageContent>
    );

    previewDialog = (
      <ProfileEditPreviewDialog
        open={edit.showPreview}
        onOpenChange={edit.setShowPreview}
        {...previewProps}
      />
    );
  }

  return (
    <AppIonPage>
      {body}
      {previewDialog}
    </AppIonPage>
  );
}
